import React from 'react'
import styled from 'styled-components'

import Icon from '../../../components/icon/Icon'
import {
  LoadingResponsive,
  LoadingWheel,
} from '../../../components/loading/Loading'
import { Text } from '../../../components/topography'
import exclaimIcon from '../../../styles/assets/exclamation-mark.svg'
import {
  fonts,
  mediaQueries,
  palette,
  shadows,
} from '../../../styles/variables'
import { translate } from '../../../utils/translation'

export const StepWrapper = styled.div((props) => ({
  width: props.width || '445px',
  maxWidth: props.maxWidth || 'auto',
  height: 'fit-content',
  minHeight: 'fit-content',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  margin: '0 auto',
  flexGrow: 1,
  [mediaQueries.mobile]: {
    width: '100%',
    height: props.small ? '350px' : '100%',
    minHeight: props.small ? '350px' : 'inherit',
    justifyContent: 'space-between',
    alignItems: 'normal',
  },
}))

export const StepContent = styled.div((props) => ({
  boxSizing: 'content-box',
  width: props?.width || '',
  [mediaQueries.laptop]: {
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}))

export const StepContentWrapper = styled.div(() => ({
  display: 'flex',
  width: '100%',
  position: 'relative',
  alignItems: 'flex-end',
  [mediaQueries.tablet]: {
    flexDirection: 'column',
  },
}))

export const NavButtons = styled.div(({ styles = {} }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '24px',
  marginBottom: '24px',
  [mediaQueries.mobile]: {
    marginBottom: 0,
    flexDirection: 'column',
    '& button': {
      width: '100%',
    },
  },
  ...styles,
}))

const NavButtonContent = styled.button((props) => {
  let backgroundColor = palette.orange
  let color = palette.white
  let borderColor = palette.orange
  const isFullGray = props.color === 'secondary'

  if (props.color === 'grey') {
    backgroundColor = palette.white
    borderColor = palette.gray
    color = palette.gray
  }
  return {
    border: `1px solid ${
      props.disabled || isFullGray ? palette.gray : borderColor
    }`,
    borderRadius: '2px',
    backgroundColor:
      props.disabled || isFullGray ? palette.gray : backgroundColor,
    fontSize: '12px',
    width: props.width ? props.width : 'fit-content',
    minWidth: '140px !important',
    height: '50px',
    lineHeight: '50px',
    color,
    fontFamily: fonts.primaryMedium,
    textTransform: 'uppercase',
    padding: '0 20px',
    letterSpacing: '2px',
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    '& + button': {
      marginLeft: '20px',
      [mediaQueries.mobile]: {
        marginLeft: 0,
        marginTop: '10px',
      },
    },
    [mediaQueries.mobile]: {
      width: '100%',
    },
  }
})

export const NavButton = ({
  color,
  onClick,
  width,
  style,
  disabled,
  testId,
  isLoading,
  tag,
}) => {
  const handleOnClick = () => {
    onClick()

    const container = document.querySelector('#add-project-modal-stepper')
    if (container) {
      container.scrollTo({
        top: 0,
      })
    }
  }

  return (
    <NavButtonContent
      color={color}
      onClick={handleOnClick}
      width={width}
      style={style}
      disabled={disabled}
      data-testid={testId || ''}
    >
      {isLoading ? (
        <LoadingResponsive
          size="30px"
          height="42px"
          offsetY="5%"
          color={color === 'orange' ? palette.white : palette.orange}
        />
      ) : (
        tag.toUpperCase()
      )}
    </NavButtonContent>
  )
}

export const InsightPanel = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'fit-content',
  boxShadow: shadows.shadowA,
  borderRadius: '4px',
  background: palette.white,
  margin: props.margin || 0,
  padding: props.padding || '24px',
  [mediaQueries.tablet]: {
    display: 'block',
  },
}))

const Steps = styled.ul({
  listStyle: 'disc',
  padding: 0,
  margin: 0,
})

const Step = styled.li((props) => ({
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  marginLeft: '20px',
  lineHeight: '24px',
  color: props.completed ? palette.gray : palette.darkGray,
  textDecoration: props.completed ? 'line-through' : 'none',
}))

const InsightDataWarning = styled.li(() => ({
  display: 'flex',
  alignItems: 'self-start',
  backgroundColor: palette.lightGray20,
  marginTop: '16px',
  padding: '24px',
  position: 'relative',
  [mediaQueries.tablet]: {
    padding: '30px 15px 15px',
    '& img': {
      position: 'absolute',
      top: '10px',
      right: '10px',
      margin: 0,
    },
  },
}))

export const InsightDataLoader = ({
  isLoading,
  hasData,
  warningText,
  subWarningTexts,
  step,
  children,
  testId,
}) => {
  if (isLoading) {
    return (
      <div
        data-testid={testId}
        style={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${palette.lightGray}`,
          borderRadius: '4px',
          marginTop: '16px',
          padding: '24px',
        }}
      >
        <LoadingWheel width={24} height={24} />
        <Text
          lineHeight="24px"
          fontFamily={fonts.primaryBook}
          color={palette.gray}
          margin="0 0 0 16px"
        >
          {translate('loading_iframe')}
        </Text>
      </div>
    )
  }
  if (hasData === false) {
    return (
      <div
        data-testid={testId}
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: palette.lightGray20,
          marginTop: '16px',
          padding: '24px',
        }}
      >
        <Text
          fontSize="16px"
          lineHeight="28px"
          color={palette.lightGray}
          dangerouslySetInnerHTML={{
            __html: translate('site_overview_not_available'),
          }}
        />
      </div>
    )
  }
  if (hasData === undefined) {
    return (
      <InsightDataWarning data-testid={testId}>
        <Icon src={exclaimIcon} size="16px" margin="2px 16px 0 0" />
        <Steps>
          <Text
            lineHeight="24px"
            color={palette.darkGray}
            fontFamily={fonts.primaryBook}
            dangerouslySetInnerHTML={{
              __html: warningText,
            }}
          />
          {subWarningTexts && (
            <>
              <Step completed={step > 1}>{subWarningTexts[0]}</Step>
              <Step>{subWarningTexts[1]}</Step>
            </>
          )}
        </Steps>
      </InsightDataWarning>
    )
  }
  return children
}

export const SiteDataContainer = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '40%',
  height: 0,
  minHeight: '100%',
  maxWidth: '600px',
  background: palette.lightGray20,
  padding: '40px',
  overflow: 'auto',
  [mediaQueries.mobile]: {
    width: '100%',
    maxWidth: '100%',
    padding: '20px',
  },
}))

export const SelectionsWrapper = styled.div({
  width: '100%',
  height: 'fit-content',
  maxHeight: '300px',
  marginTop: '24px',
  overflowY: 'auto',
  [mediaQueries.mobile]: {
    maxHeight: 'inherit',
    overflowY: 'inherit',
  },
})

export const SelectedLocation = styled.div(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  height: '50px',
  border: `1px solid ${palette.lightGray}`,
  borderRadius: '2px',
  background: palette.white,
  paddingLeft: '16px',
  cursor: 'default',
  width: '100%',
  margin: '10px 0',
  ':first-child': {
    marginTop: 0,
  },
  ':last-child': {
    marginBottom: 10,
  },
  [mediaQueries.mobile]: {
    '& span:last-child': {
      fontSize: '12px',
    },
  },
}))
